
















































































import Vue from 'vue'
import Component from 'vue-class-component'
import { loggerFor } from '@/shared/utils/logger'
import { Inject, Prop } from 'vue-property-decorator'
import { Injector } from '@/config/dependency-injection'
import { NavigationGuard, VueRouter } from 'vue-router/types/router'
import { RouterToken } from '@/config/injection-tokens'
import UnifyButton from '@/shared/components/unify/unify-button.vue'
import { Just, Maybe } from 'purify-ts'
import { Hooks } from '@/shared/interfaces/hooks'
import { FormGroup } from '@tf/forms'
import { Step } from '@/shared/tf/stepper/step'
import { Steppable } from '@/shared/interfaces/steppable'

@Component({
  components: { UnifyButton },
})
export default class TfStepper extends Vue implements Hooks, Steppable {
  @Prop({
    default: null,
    type: Number,
  })
  public readonly active!: number | null

  @Prop({
    default: true,
    type: Boolean,
  })
  public readonly header!: boolean

  @Prop({
    default: false,
    type: Boolean,
  })
  public readonly noControls!: boolean

  @Prop({
    default: false,
    type: Boolean,
  })
  public readonly noHeader!: boolean

  @Prop({
    default: false,
    type: Boolean,
  })
  public readonly pending!: boolean

  @Prop({
    default: true,
    type: Boolean,
  })
  public readonly submitLastStep!: boolean

  @Prop({
    default: 'default',
    type: String,
    validator(value: string): boolean {
      return ['default', 'simple'].indexOf(value) > -1
    },
  })
  public readonly type!: 'default' | 'simple'

  @Prop({
    default: false,
    type: Boolean,
  })
  public readonly updateRoute!: boolean

  @Inject({
    default: undefined,
  })
  public readonly form?: FormGroup<any>

  public activeStepIndex = 0

  private logger = loggerFor('TfStepper')
  private router = Injector.get<VueRouter>(RouterToken)
  private steps: Step[] = []

  get activeStep(): Step | undefined {
    return this.steps[this.activeStepIndex]
  }

  get activeStepId(): string | undefined {
    return this.activeStep?.id
  }

  get activeStepIsInvalid(): boolean {
    let invalid = false

    const control = this.activeStep?.control

    if (control && this.form) {
      invalid = this.form.get(control).invalid
    }

    return invalid
  }

  get controls(): boolean {
    return this.noControls !== true
  }

  get isLastStep(): boolean {
    return this.activeStepIndex === this.steps.length - 1
  }

  public data() {
    return {
      currentStep: undefined,
    }
  }

  public beforeRouteUpdate: NavigationGuard = to => {
    this.updateStepByName(to.params.activeStep)
  }

  public mounted() {
    this.steps = Maybe.mapMaybe(
      value => Just((value.componentInstance as unknown) as Step),
      this.$slots.default!
    )

    if (this.active != null) {
      this.select(this.active)
    }

    if (this.updateRoute === true) {
      this.updateStepByName(this.router.currentRoute.params.activeStep)
    }
  }

  public provide() {
    return {
      stepper: this,
    }
  }

  public back() {
    let next = this.activeStepIndex

    if (this.activeStepIndex > 0) {
      --next
    }

    this.select(next)
  }

  public next() {
    let next = this.activeStepIndex

    if (this.activeStepIndex < this.steps.length) {
      ++next
    }

    this.select(next)
  }

  public select(stepIdOrIndex: number | string) {
    let stepIndex: number | undefined

    if (typeof stepIdOrIndex === 'string') {
      // Try to find a step with an id matching the given parameter
      const index = this.steps.findIndex(
        step => step.id === stepIdOrIndex || step.control === stepIdOrIndex
      )

      if (index === -1) {
        this.logger.warn(`Cannot find a step with the id or control name ${stepIdOrIndex}`)
      } else {
        stepIndex = index
      }
    } else {
      stepIndex = stepIdOrIndex
    }

    if (stepIndex !== undefined) {
      this.toggleActiveStep(false)

      this.activeStepIndex = stepIndex

      this.toggleActiveStep(true)

      this.$emit('change', stepIndex)

      this.updateLocation()
    }
  }

  public stepWidth(index: number): string {
    let stepWidth = `calc(${100 / (this.steps.length - 1)}% - ${12 / (this.steps.length - 1)}px)`

    if (index >= this.steps.length - 1) {
      stepWidth = '12px'
    }

    return stepWidth
  }

  public submit(): void {
    this.$emit('submit')
  }

  private toggleActiveStep(force?: boolean): void {
    if (this.activeStep) {
      this.activeStep.toggleActive(force)
    }
  }

  private updateLocation() {
    if (this.updateRoute === true) {
      const route = {
        ...this.router.currentRoute,
        name: this.router.currentRoute.name ?? undefined,
      }

      const stepName = this.activeStep?.id || this.activeStep?.title

      if (stepName === undefined) {
        this.logger.warn(
          'Missing an id or a title to update the active step name of the current route'
        )
      } else {
        route.params = {
          activeStep: stepName,
        }
      }

      this.router.replace(route)
    }
  }

  private updateStepByName(name?: string) {
    let activeStep = 0

    if (name !== undefined) {
      for (let i = 0; i < this.steps.length; ++i) {
        const step = this.steps[i]

        if (step.id === name || step.title === name) {
          activeStep = i
          break
        }
      }
    }

    this.activeStepIndex = activeStep
  }
}
